/**
 * Leads Hook - per-run lead counts, asked from the server
 *
 * 2026-10-30: count queries, refreshed while the page is open. Before, the dashboard kept a live listener on the
 * client's WHOLE leads collection (6,791 to 22,908 leads), and every change cost the Firebase SDK a pass over all of
 * them. A big send changes each lead with its own write (markAcceptedLeads.ts), so the page froze for over a minute
 * in testing and the send's answer could arrive after the browser's 70 s timer: the send worked, but the client saw
 * "Error: deadline-exceeded" (TM2, Tony Tomlinson). It also read every lead each time the dashboard opened.
 * The queries use the (run, status, nextEligibleCallAt) index the dispatcher already needs.
 */

import { useEffect, useState } from 'react';
import { collection, getCountFromServer, getDocs, limit, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useEffectiveClientId } from './useImpersonation';
import { emptyRunCounts, LEADS_CHANGED_EVENT, nextEligibleToMs, RUN_NUMBERS, RunCounts, runCountFrom } from './leadRunCounts';

export type { RunCount } from './leadRunCounts';

const COUNT_REFRESH_MS = 30000;
/** No counts on screen yet: try again soon, so a client does not see "0 available" for a whole refresh. */
const FIRST_LOAD_RETRY_MS = 5000;

async function loadRunCounts(clientId: string): Promise<RunCounts> {
  const leadsRef = collection(db, 'clients', clientId, 'leads');
  const now = Timestamp.now();
  const counts = emptyRunCounts();
  await Promise.all(RUN_NUMBERS.map(async run => {
    const pendingInRun = query(leadsRef, where('run', '==', run), where('status', '==', 'pending'));
    const pending = (await getCountFromServer(pendingInRun)).data().count;
    if (pending === 0) return;
    const waitingInRun = query(pendingInRun, where('nextEligibleCallAt', '>', now));
    const waiting = (await getCountFromServer(waitingInRun)).data().count;
    let earliest: number | null = null;
    if (waiting > 0) {
      const first = await getDocs(query(waitingInRun, orderBy('nextEligibleCallAt'), limit(1)));
      earliest = nextEligibleToMs(first.docs[0]?.get('nextEligibleCallAt'));
    }
    counts[run] = runCountFrom(pending, waiting, earliest);
  }));
  return counts;
}

export function useLeads() {
  const effectiveClientId = useEffectiveClientId();
  const [runCounts, setRunCounts] = useState<RunCounts>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!effectiveClientId) {
      setLoading(false);
      return;
    }

    setRunCounts({});
    setLoading(true);
    let stopped = false;
    let inFlight = false;
    let again = false;
    let hasCounts = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const refresh = async () => {
      if (stopped) return;
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      try {
        const counts = await loadRunCounts(effectiveClientId);
        hasCounts = true;
        if (!stopped) setRunCounts(counts);
      } catch (error) {
        // Keep the last good counts on screen; the next refresh tries again.
        console.error('Lead counts failed:', error);
        if (!hasCounts && !stopped) retryTimer = setTimeout(refresh, FIRST_LOAD_RETRY_MS);
      } finally {
        inFlight = false;
        if (!stopped) setLoading(false);
        if (again) {
          again = false;
          void refresh();
        }
      }
    };

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };

    void refresh();
    const timer = setInterval(refreshIfVisible, COUNT_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener(LEADS_CHANGED_EVENT, refreshIfVisible);

    return () => {
      stopped = true;
      clearInterval(timer);
      if (retryTimer !== null) clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener(LEADS_CHANGED_EVENT, refreshIfVisible);
    };
  }, [effectiveClientId]);

  return { runCounts, loading };
}
